/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, SignalState } from '@ngrx/signals';

import { ArrayUtils } from '@spaces-ui/utils';

export interface LoadableEntitiesSignalState<T> {
  isLoading: boolean;
  entities: T[];
  entityMap: Record<string, T>;
  idKey: keyof T;
  hasError: boolean;
  isPaged: boolean;
  error?: string;
  pagesRetrieved: number;
  pendingPages: number[];
  allPagesLoaded: boolean;
}

export class LoadableEntitiesUtils {
  private static notificationService: {
    showSuccess: (message: string) => void;
    showError: (message: string, error: string) => void;
  };

  public static registerNotificationService(notificationService: {
    showSuccess: (message: string) => void;
    showError: (message: string, error: string) => void;
  }) {
    LoadableEntitiesUtils.notificationService = notificationService;
  }

  public static createInitialState<T>(
    options: Partial<LoadableEntitiesSignalState<T>> = {},
  ): LoadableEntitiesSignalState<T> {
    const state: LoadableEntitiesSignalState<T> = {
      isLoading: false,
      entities: [],
      entityMap: {},
      idKey: 'id' as keyof T,
      hasError: false,
      isPaged: false,
      pagesRetrieved: 0,
      pendingPages: [],
      allPagesLoaded: false,
      ...options,
    };

    return state;
  }

  public static createPagedInitialState<T>(
    options: Partial<LoadableEntitiesSignalState<T>> = {},
  ): LoadableEntitiesSignalState<T> {
    const state: LoadableEntitiesSignalState<T> = {
      isLoading: false,
      entities: [],
      entityMap: {},
      idKey: 'id' as keyof T,
      hasError: false,
      isPaged: true,
      pagesRetrieved: 0,
      pendingPages: [],
      allPagesLoaded: false,
      ...options,
    };

    return state;
  }

  public static async setEntities<EntityType extends object, StateType extends object>(
    entities: EntityType[],
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    replaceState = false,
  ) {
    const op = replaceState
      ? this.replaceEntities(Promise.resolve(entities.map(e => e)), state, signalKey)
      : this.loadEntities(Promise.resolve(entities.map(e => e)), state, signalKey);

    return await op;
  }

  /**
   * Gap-free commit variant of setEntities: no API request, no error path, and exactly one
   * synchronous patchState with no awaits. Use it when the write must land atomically within
   * the current macrotask - an awaited commit leaves a microtask gap in which another handler's
   * synchronous patch can land and then be overwritten by the pre-await snapshot.
   * Same merge semantics as loadEntities (incoming wins by id, other entities kept), including
   * the error-state reset and uniqueBy dedupe.
   */
  public static setEntitiesSync<EntityType extends object, StateType extends object>(
    entities: EntityType[],
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    replaceState = false,
  ) {
    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;
    const idKey = currentState.idKey;

    let merged: EntityType[];
    if (replaceState) {
      merged = [...entities];
    } else {
      const incomingIds = new Set(entities.map(e => e[idKey]));
      merged = [...currentState.entities.filter(e => !incomingIds.has(e[idKey])), ...entities];
    }

    const uniqueEntities = ArrayUtils.uniqueBy(merged, idKey);

    patchState(state as any, {
      [signalKey]: {
        ...currentState,
        isLoading: false,
        hasError: false,
        error: undefined,
        entities: uniqueEntities,
        entityMap: Object.fromEntries(
          uniqueEntities.map(entity => [entity[idKey] as string, entity]),
        ),
      },
    });
  }

  /**
   * Gap-free single-entity variant of setEntity - see setEntitiesSync. Use it from socket
   * handlers that read a snapshot and write it back: several frames in one flush batch each
   * read the same pre-commit snapshot, so an awaited commit lets the last one clobber the rest.
   */
  public static setEntitySync<EntityType extends object, StateType extends object>(
    entity: EntityType,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
  ) {
    this.setEntitiesSync([entity], state, signalKey);
  }

  public static async setEntity<EntityType, StateType extends object>(
    entity: EntityType,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
  ) {
    return await this.loadEntity(Promise.resolve({ ...entity }), state, signalKey);
  }

  public static async setPendingPages<EntityType, StateType extends object>(
    pendingPages: number[],
    state: SignalState<StateType>,
    signalKey: keyof StateType,
  ) {
    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;

    patchState(state as any, { [signalKey]: { ...currentState, pendingPages } });
  }

  public static async setPagesRetrieved<EntityType, StateType extends object>(
    pagesRetrieved: number,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
  ) {
    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;

    patchState(state as any, { [signalKey]: { ...currentState, pagesRetrieved } });
  }

  public static async setAllPagesLoaded<EntityType, StateType extends object>(
    allPagesLoaded: boolean,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
  ) {
    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;

    patchState(state as any, { [signalKey]: { ...currentState, allPagesLoaded } });
  }

  public static async deleteEntity<StateType extends object>(
    entityId: string,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
  ) {
    return await this.removeEntity(Promise.resolve(), entityId, state, signalKey);
  }

  public static async deleteEntities<StateType extends object>(
    entityIds: string[],
    state: SignalState<StateType>,
    signalKey: keyof StateType,
  ) {
    return await this.removeEntities(Promise.resolve(), entityIds, state, signalKey);
  }

  /**
   * Load entities and replace the current state.
   * NOTE this removes existing entities from the list
   */
  public static async replaceEntities<EntityType extends object, StateType extends object>(
    apiReq: Promise<EntityType[]>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options?: {
      errorMessage?: string;
      successMessage?: string;
      transform?: (entities: EntityType[]) => EntityType[];
    },
  ) {
    return await this._loadEntities(
      apiReq,
      state,
      signalKey,
      options?.errorMessage,
      options?.successMessage,
      options?.transform,
      true,
    );
  }

  /**
   * Load entities and append them to the current state.
   * NOTE this does not remove existing entities from the list
   */
  public static async loadEntities<EntityType extends object, StateType extends object>(
    apiReq: Promise<EntityType[]>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options?: {
      errorMessage?: string;
      successMessage?: string;
      transform?: (entities: EntityType[]) => EntityType[];
    },
  ) {
    return await this._loadEntities(
      apiReq,
      state,
      signalKey,
      options?.errorMessage,
      options?.successMessage,
      options?.transform,
      false,
    );
  }

  public static clearEntities<EntityType, StateType extends object>(
    state: SignalState<StateType>,
    signalKey: keyof StateType,
  ) {
    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;

    patchState(state as any, { [signalKey]: { ...currentState, entities: [], entityMap: {} } });
  }

  public static async setLoadingState<EntityType, StateType extends object>(
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    loading: boolean,
  ) {
    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;

    patchState(state as any, { [signalKey]: { ...currentState, isLoading: loading } });
  }

  public static async loadEntity<EntityType, StateType extends object>(
    apiReq: Promise<EntityType>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options?: {
      errorMessage?: string;
      successMessage?: string;
    },
  ) {
    this.resetErrorState(state, signalKey, false);

    const results = await this.makeApiRequest(
      apiReq,
      state,
      signalKey,
      options?.errorMessage,
      options?.successMessage,
    );

    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;
    const idKey = currentState.idKey;

    const entities = [...currentState.entities.filter(e => e[idKey] !== results[idKey]), results];

    patchState(state as any, {
      [signalKey]: {
        ...currentState,
        isLoading: false,
        entities: entities,
        entityMap: Object.fromEntries(entities.map(entity => [entity[idKey] as string, entity])),
      },
    });
  }

  public static async addAndRetrieveEntity<EntityType, StateType extends object>(
    postApiReq: Promise<string>,
    getApiReq: (id: string) => Promise<EntityType>,
    newEntity: Partial<EntityType>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options?: {
      errorMessage?: string;
      successMessage?: string;
    },
  ): Promise<string> {
    return this._addEntity(postApiReq, newEntity, state, signalKey, {
      ...(options ?? {}),
      getEntityApi: getApiReq,
    });
  }
  public static async addEntity<EntityType, StateType extends object>(
    apiReq: Promise<string>,
    newEntity: Partial<EntityType>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options?: {
      errorMessage?: string;
      successMessage?: string;
    },
  ): Promise<string> {
    return this._addEntity(apiReq, newEntity, state, signalKey, {
      ...(options ?? {}),
    });
  }

  public static async updateAndRetrieveEntity<EntityType, StateType extends object>(
    updateApiReq: Promise<unknown>,
    getApiReq: (id: string) => Promise<EntityType>,
    entityToUpdate: Partial<EntityType>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options: {
      updateMethod: 'replace' | 'merge';
      errorMessage?: string;
      successMessage?: string;
    },
  ) {
    await this._updateEntity(updateApiReq, entityToUpdate, state, signalKey, {
      ...options,
      getEntityApi: getApiReq,
    });
  }

  public static async updateEntity<EntityType, StateType extends object>(
    apiReq: Promise<unknown>,
    entityToUpdate: Partial<EntityType>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options: {
      updateMethod: 'replace' | 'merge';
      errorMessage?: string;
      successMessage?: string;
    },
  ) {
    await this._updateEntity(apiReq, entityToUpdate, state, signalKey, {
      ...options,
    });
  }

  public static async removeEntities<EntityType, StateType extends object>(
    apiReq: Promise<unknown>,
    entityIds: string[],
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options?: {
      errorMessage?: string;
      successMessage?: string;
    },
  ) {
    this.resetErrorState(state, signalKey, false);

    await this.makeApiRequest(
      apiReq,
      state,
      signalKey,
      options?.errorMessage,
      options?.successMessage,
    );

    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;
    const idKey = currentState.idKey;
    const entities = currentState.entities.filter(e => !entityIds.includes(e[idKey] as string));

    patchState(state as any, {
      [signalKey]: {
        ...currentState,
        isLoading: false,
        entities,
        entityMap: Object.fromEntries(entities.map(entity => [entity[idKey] as string, entity])),
      },
    });
  }

  public static async removeEntity<EntityType, StateType extends object>(
    apiReq: Promise<unknown>,
    entityId: string,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options?: {
      errorMessage?: string;
      successMessage?: string;
    },
  ) {
    this.resetErrorState(state, signalKey, false);

    await this.makeApiRequest(
      apiReq,
      state,
      signalKey,
      options?.errorMessage,
      options?.successMessage,
    );

    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;
    const idKey = currentState.idKey;
    const entities = currentState.entities.filter(e => e[idKey] !== entityId);

    patchState(state as any, {
      [signalKey]: {
        ...currentState,
        isLoading: false,
        entities,
        entityMap: Object.fromEntries(entities.map(entity => [entity[idKey] as string, entity])),
      },
    });
  }

  private static async _loadEntities<EntityType extends object, StateType extends object>(
    apiReq: Promise<EntityType[]>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    errorMessage?: string,
    successMessage?: string,
    transform?: (entities: EntityType[]) => EntityType[],
    replaceState = true,
  ) {
    this.resetErrorState(state, signalKey, true);

    const results = await this.makeApiRequest(
      apiReq,
      state,
      signalKey,
      errorMessage,
      successMessage,
    );

    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;

    let entities: EntityType[];
    if (replaceState) {
      entities = [...results];
    } else {
      const resultIds = new Set(results.map(r => r[currentState.idKey]));
      entities = [
        ...currentState.entities.filter(e => !resultIds.has(e[currentState.idKey])),
        ...results,
      ];
    }

    if (transform) {
      entities = transform(structuredClone(entities));
    }

    const uniqueEntities = ArrayUtils.uniqueBy(entities, currentState.idKey);

    patchState(state as any, {
      [signalKey]: {
        ...currentState,
        isLoading: false,
        entities: uniqueEntities,
        entityMap: Object.fromEntries(
          uniqueEntities.map(entity => [entity[currentState.idKey] as string, entity]),
        ),
      },
    });
  }

  private static async _addEntity<EntityType, StateType extends object>(
    apiReq: Promise<string>,
    newEntity: Partial<EntityType>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options?: {
      errorMessage?: string;
      successMessage?: string;
      getEntityApi?: (id: string) => Promise<EntityType>;
    },
  ): Promise<string> {
    this.resetErrorState(state, signalKey, false);

    const newId = await this.makeApiRequest(
      apiReq,
      state,
      signalKey,
      options?.errorMessage,
      options?.successMessage,
    );

    if (options?.getEntityApi) {
      await this.loadEntity(options.getEntityApi(newId), state, signalKey, options);
    } else {
      const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;
      const idKey = currentState.idKey;
      const entities = [
        ...currentState.entities.filter(e => e[idKey] !== newId),
        { ...newEntity, [idKey]: newId } as EntityType,
      ];

      patchState(state as any, {
        [signalKey]: {
          ...currentState,
          isLoading: false,
          entities,
          entityMap: Object.fromEntries(entities.map(entity => [entity[idKey] as string, entity])),
        },
      });
    }

    return newId;
  }

  private static async _updateEntity<EntityType, StateType extends object>(
    apiReq: Promise<unknown>,
    entityToUpdate: Partial<EntityType>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    options: {
      updateMethod: 'replace' | 'merge';
      errorMessage?: string;
      successMessage?: string;
      getEntityApi?: (id: string) => Promise<EntityType>;
    },
  ) {
    this.resetErrorState(state, signalKey, false);

    await this.makeApiRequest(
      apiReq,
      state,
      signalKey,
      options.errorMessage,
      options.successMessage,
    );

    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;
    const idKey = currentState.idKey;

    if (options?.getEntityApi) {
      await this.loadEntity(
        options.getEntityApi(entityToUpdate[idKey] as string),
        state,
        signalKey,
        options,
      );
    } else {
      const currentEntity = currentState.entityMap[entityToUpdate[idKey] as string];
      let entities: EntityType[];

      if (options.updateMethod === 'replace') {
        entities = [
          ...currentState.entities.filter(e => e[idKey] !== entityToUpdate[idKey]),
          { ...entityToUpdate } as EntityType,
        ];
      } else {
        entities = [
          ...currentState.entities.filter(e => e[idKey] !== entityToUpdate[idKey]),
          { ...currentEntity, ...entityToUpdate } as EntityType,
        ];
      }

      patchState(state as any, {
        [signalKey]: {
          ...currentState,
          isLoading: false,
          entities,
          entityMap: Object.fromEntries(entities.map(entity => [entity[idKey] as string, entity])),
        },
      });
    }
  }

  private static resetErrorState<EntityType, StateType extends object>(
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    markAsLoading: boolean,
  ) {
    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;

    patchState(state as any, {
      [signalKey]: { ...currentState, isLoading: markAsLoading, hasError: false, error: undefined },
    });
  }

  private static async makeApiRequest<APIResponseType, EntityType, StateType extends object>(
    apiReq: Promise<APIResponseType>,
    state: SignalState<StateType>,
    signalKey: keyof StateType,
    errorMessage?: string,
    successMessage?: string,
  ) {
    const currentState = (state as any)[signalKey]() as LoadableEntitiesSignalState<EntityType>;

    const results = await apiReq.catch((error: HttpErrorResponse) => {
      patchState(state as any, {
        [signalKey]: { ...currentState, isLoading: false, hasError: true, error: error.message },
      });

      if (LoadableEntitiesUtils.notificationService && errorMessage) {
        LoadableEntitiesUtils.notificationService.showError(errorMessage, error.error);
      }

      throw error;
    });

    if (LoadableEntitiesUtils.notificationService && successMessage) {
      LoadableEntitiesUtils.notificationService.showSuccess(successMessage);
    }

    return results;
  }
}
